'use client'

import cx from 'classix'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import dynamic from 'next/dynamic'
import { type JSX, useEffect, useRef, useState } from 'react'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'
import { useSimpleVideoOrientation } from './hooks/useSimpleVideoOrientation'
import styles from './SimpleVideo.module.css'
import type { PointOfInterestImage, Video } from './types/SimpleVideoTypes'

type SimpleVideoProps = {
	readonly disableAutoplay: boolean
	readonly preloadVideo?: 'auto' | 'none'
	readonly videoButtonClassName?: string
	readonly videoButtonTitle?: {
		play: string
		pause: string
	}
	readonly video?: {
		videoPortrait?: Video
		videoLandscape?: Video
		videoPlaceholder?: PointOfInterestImage | null
	}
	readonly inverse?: boolean
	readonly loop?: boolean
	readonly onVideoCanPlay?: () => void
	readonly contentIfVideoCantPlay?: JSX.Element | null
	readonly hidePlayButtonControl?: boolean
	readonly videoClassName?: string
	readonly videoWrapperClassName?: string
}

const SimpleVideoButton = dynamic(() =>
	import('./SimpleVideoButton').then((mod) => mod.SimpleVideoButton)
)

export const REDUCED_MOTION_NO_PREFERENCE_QUERY =
	'(prefers-reduced-motion: no-preference)'

export function SimpleVideo({
	disableAutoplay,
	preloadVideo = 'auto',
	videoButtonClassName,
	videoButtonTitle,
	video = {},
	loop = true,
	contentIfVideoCantPlay,
	onVideoCanPlay,
	hidePlayButtonControl = false,
	videoClassName,
	videoWrapperClassName,
}: SimpleVideoProps) {
	const { videoPortrait, videoLandscape } = video
	const isNoPreferenceMotion =
		// biome-ignore lint/complexity/useOptionalChain: Can't use on window
		typeof window !== 'undefined' &&
		window?.matchMedia(REDUCED_MOTION_NO_PREFERENCE_QUERY).matches
	const videoRef = useRef<HTMLVideoElement>(null)
	const videoContainerRef = useRef<HTMLDivElement>(null)
	const entry = useIntersectionObserver(videoContainerRef, {
		threshold: 0.25,
	})
	const { hasChangedOrientation, showPortraitVideo, currentTime } =
		useSimpleVideoOrientation(videoRef)
	const isVideoVisible = Boolean(entry?.isIntersecting)
	const allowVideo = typeof entry?.isIntersecting === 'boolean'
	const isAutoPlay = isNoPreferenceMotion && isVideoVisible && !disableAutoplay

	const [canPlay, setCanPlay] = useState<boolean>(false)
	const [isPlayClicked, setIsPlayClicked] = useState<boolean>(false)
	const [isPlaying, setIsPlaying] = useState<boolean>(false)

	const autoPlay = hasChangedOrientation ? isPlaying : isAutoPlay
	const sourceVideo = showPortraitVideo ? videoPortrait : videoLandscape

	useEffect(() => {
		if (allowVideo) {
			setIsPlayClicked(isNoPreferenceMotion)
			setIsPlaying(isAutoPlay)
			if (isAutoPlay && videoRef.current) {
				videoRef.current.play()
			}
		}
	}, [allowVideo])

	useEffect(() => {
		if (videoRef.current && hasChangedOrientation) {
			videoRef.current.currentTime = currentTime
			if (isPlaying) {
				videoRef.current.play()
			}
		}
	}, [hasChangedOrientation])

	useEffect(() => {
		if (!videoRef.current) {
			return
		}
		if (isVideoVisible && isPlayClicked && !isPlaying) {
			videoRef.current.play()
			setIsPlaying(true)
		} else if (!isVideoVisible && isPlaying) {
			videoRef.current.pause()
			setIsPlaying(false)
		} else {
			return
		}
	}, [isVideoVisible])

	const handlePlayPause: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault()
		e.stopPropagation()
		if (!videoRef.current) {
			return
		}
		if (isPlaying) {
			videoRef.current.pause()
			setIsPlayClicked(false)
			setIsPlaying(false)
		} else {
			videoRef.current.play()
			setIsPlayClicked(true)
			setIsPlaying(true)
		}
	}

	const handleVideoEnd = () => {
		setIsPlayClicked(false)
	}

	const handleVideoCanPlay = () => {
		setCanPlay(true)
		if (onVideoCanPlay) {
			onVideoCanPlay()
		}
	}

	return (
		<div
			className={cx(styles.videoContainer, videoWrapperClassName)}
			ref={videoContainerRef}
		>
			{allowVideo && (
				<video
					key={sourceVideo?.url}
					preload={preloadVideo}
					autoPlay={autoPlay}
					loop={loop}
					muted
					playsInline
					ref={videoRef}
					onEnded={handleVideoEnd}
					className={cx(
						styles.video,
						videoClassName,
						applyStylesIf(!canPlay, styles.hidden)
					)}
					data-testid='landings.home.heroBannerShop.video'
					onCanPlay={handleVideoCanPlay}
					onContextMenu={(event) => {
						event.preventDefault()
						event.stopPropagation()
					}}
				>
					<source
						src={sourceVideo?.url}
						type={sourceVideo?.contentType}
						data-testid='landings.home.heroBannerShop.source'
					/>
					<track kind='captions' default />
				</video>
			)}
			{!canPlay && contentIfVideoCantPlay}
			{canPlay && !hidePlayButtonControl && (
				<SimpleVideoButton
					videoButtonClassName={videoButtonClassName}
					videoButtonTitle={videoButtonTitle}
					isPlaying={isPlaying}
					handlePlayPause={handlePlayPause}
				/>
			)}
		</div>
	)
}
